/**
 * Worker message protocol (ARCHITECTURE, "Worker protocol").
 * Structured-clone messages, discriminated on `type`.
 */

import type { Action, RunLog } from '../engine/actions';
import type { ScoreCard } from '../engine/scoring';
import type { SaveFile, Snapshot } from '../engine/snapshot';
import type { Difficulty } from '../engine/types';
import type { SandboxOptions } from '../scenarios/sandbox';
import type { WhatIfRequest, WhatIfTrace } from './whatif';

/** Sim speeds, as multiples of real time (§3.1). 3,600× is sandbox/fast-forward only. */
export const SPEEDS = [1, 12, 60, 300, 900, 3600] as const;
export type Speed = (typeof SPEEDS)[number];

export type ToWorker =
  | {
      type: 'init';
      siteId: string;
      scenarioId: string;
      difficulty: Difficulty;
      seed: number;
      /** Present only for the sandbox, whose config is built from the launcher's choices. */
      sandbox?: SandboxOptions | null;
    }
  | { type: 'load'; save: SaveFile }
  /** `site` selects which plant the actions go to; omitted means the primary (upstream) plant. */
  | { type: 'dispatch'; actions: Action[]; site?: SiteSlot }
  | { type: 'setSpeed'; speed: Speed }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'requestSave'; name: string }
  | { type: 'whatIf'; id: number; requests: WhatIfRequest[] }
  | { type: 'cancelWhatIf' }
  | { type: 'requestSnapshot' };

/** Which plant a message concerns. Only cascade scenarios have a secondary. */
export type SiteSlot = 'primary' | 'secondary';

export type FromWorker =
  | { type: 'ready' }
  | { type: 'snapshot'; snapshot: Snapshot; secondary: Snapshot | null }
  | {
      type: 'scenarioEnd';
      card: ScoreCard;
      snapshot: Snapshot;
      secondaryCard: ScoreCard | null;
      secondarySnapshot: Snapshot | null;
    }
  | { type: 'whatIfResult'; id: number; traces: WhatIfTrace[] }
  | { type: 'saved'; save: SaveFile; runLog: RunLog }
  | { type: 'fault'; message: string };
